import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, } from 'react';
import { useAuth, useConfig } from '@marvelapp/ballpark-application';
import { useChatWithStorage } from './useChatWithStorage';
const ChatContext = createContext(null);
export function ChatProvider({ children }) {
    const config = useConfig();
    const edgeHost = config.edge.host;
    const accessToken = useAuth();
    const contextCallbacks = useRef(new Set());
    const registerContextCallback = useCallback((callback) => {
        contextCallbacks.current.add(callback);
    }, []);
    const unregisterContextCallback = useCallback((callback) => {
        contextCallbacks.current.delete(callback);
    }, []);
    const getChatContext = useCallback(() => {
        const result = {};
        contextCallbacks.current.forEach((callback) => {
            const data = callback();
            Object.assign(result, data);
        });
        return result;
    }, []);
    const chat = useChatWithStorage({
        maxSteps: 5,
        api: `${edgeHost}/api/chat`,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return (_jsx(ChatContext.Provider, { value: {
            chat,
            registerContextCallback,
            unregisterContextCallback,
            getChatContext,
        }, children: children }));
}
export function useChat() {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context.chat;
}
export const useRegisterChatContext = (callback) => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useRegisterChatContext must be used within a ChatProvider');
    }
    useEffect(() => {
        context.registerContextCallback(callback);
        return () => {
            context.unregisterContextCallback(callback);
        };
    }, [callback, context]);
};
export const useGetChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useGetChatContext must be used within a ChatProvider');
    }
    return context.getChatContext;
};
